/**
 * LWD Editorial, design tokens
 *
 * Separate from magazineTheme.js intentionally.
 * The Editorial system is a fresh presentation layer, not a reskin.
 *
 * Type scale:
 *   Hero title   clamp(44px, 7vw, 110px)  , Gilda Display
 *   Section h    clamp(28px, 3.5vw, 52px) , Gilda Display
 *   Card title   clamp(18px, 2vw, 28px)   , Gilda Display
 *   Body         18px / 1.88              , Georgia
 *   Labels       9px / 0.28em caps        , Nunito
 */

export const GOLD  = '#c9a96e';
export const CREAM = '#f5f0e8';

/** Dark editorial body, richer/deeper than the magazine dark */
export const DARK = {
  bg:      '#070604',
  surface: '#0e0c09',
  card:    '#111009',
  text:    '#f0ebe0',
  body:    'rgba(240,235,224,0.86)',
  muted:   'rgba(240,235,224,0.48)',
  subtle:  'rgba(240,235,224,0.28)',
  border:  'rgba(240,235,224,0.07)',
  border2: 'rgba(240,235,224,0.13)',
  gold:    GOLD,
  goldDim: `${GOLD}22`,
};

/** Light editorial body, warm cream, not clinical */
export const LIGHT = {
  bg:      '#f7f4ef',
  surface: '#ffffff',
  card:    '#f2ede4',
  text:    '#16140f',
  body:    '#433939',
  muted:   'rgba(22,20,15,0.52)',
  subtle:  'rgba(22,20,15,0.30)',
  border:  'rgba(22,20,15,0.08)',
  border2: 'rgba(22,20,15,0.14)',
  gold:    GOLD,
  goldDim: `${GOLD}22`,
};

/** @param {boolean} dark */
export const getTheme = (dark = true) => dark ? DARK : LIGHT;

/** Typography families */
export const FD = "'Gilda Display', 'Playfair Display', Georgia, serif";
export const FU = "'Nunito', 'Inter', 'Helvetica Neue', sans-serif";
export const FS = "'Nunito', 'Inter', 'Helvetica Neue', sans-serif";

/** Template type IDs */
export const TEMPLATES = {
  CINEMATIC:          'cinematic',
  COVER:              'cover',
  EDITORIAL_OPENER:   'editorial_opener',
  PORTRAIT:           'portrait',
  DOCUMENTARY_SPREAD: 'documentary_spread',
};

/** Category metadata for editorial navigation.
 *  The Edit chapters (Places & People): Our Venues + Our Creatives lead, then
 *  Real Weddings, Destinations, Perspectives, Fashion. Ids match
 *  magazine_categories.slug so the feed + category pages filter correctly. */
export const EDITORIAL_CATEGORIES = [
  { id: 'our-venues',    label: 'Our Venues',    accent: '#c9a96e' },
  { id: 'our-creatives', label: 'Our Creatives', accent: '#b8a9c9' },
  // 'editorial' was missing here while carrying 23 published articles, so the
  // category hub fell back to rendering its raw slug and shipped an <h1> reading
  // "editorial" in lowercase. Added 2026-08-15. Note real-weddings was listed
  // and has zero published articles, the exact inverse of the real state.
  { id: 'editorial',     label: 'Editorial',     accent: '#a89f91' },
  { id: 'real-weddings', label: 'Real Weddings', accent: '#c9a96e' },
  { id: 'destinations',  label: 'Destinations',  accent: '#7ba7bc' },
  { id: 'perspectives',  label: 'Perspectives',  accent: '#9b8bb4' },
  { id: 'fashion',       label: 'Fashion',       accent: '#d4a5a5' },
];

/** Tag → magazine category mapping for tag navigation */
export const TAG_CATEGORY_MAP = {
  // Geography → destinations
  'italy': 'destinations', 'tuscany': 'destinations', 'amalfi': 'destinations',
  'amalfi coast': 'destinations', 'france': 'destinations', 'paris': 'destinations',
  'provence': 'destinations', 'lake como': 'destinations', 'lake-como': 'destinations',
  'zanzibar': 'destinations', 'maldives': 'destinations', 'bali': 'destinations',
  'greece': 'destinations', 'santorini': 'destinations', 'mykonos': 'destinations',
  'spain': 'destinations', 'portugal': 'destinations', 'scotland': 'destinations',
  'destination wedding': 'destinations', 'destination-wedding': 'destinations',
  // Venues
  'venue': 'our-venues', 'wedding venue': 'our-venues', 'chateau': 'our-venues',
  'château': 'our-venues', 'villa': 'our-venues', 'palace': 'our-venues',
  // Creatives
  'planner': 'our-creatives', 'wedding planner': 'our-creatives',
  'photographer': 'our-creatives', 'videographer': 'our-creatives',
  'florist': 'our-creatives', 'entertainment': 'our-creatives',
  // Fashion
  'fashion': 'fashion', 'bridal': 'fashion', 'couture': 'fashion', 'dress': 'fashion',
  // Real weddings
  'real wedding': 'real-weddings', 'real-wedding': 'real-weddings',
  // Travel/honeymoon
  'honeymoon': 'honeymoons', 'travel': 'travel',
};

const VALID_CAT_IDS = new Set(EDITORIAL_CATEGORIES.map(c => c.id));

/** Resolve a freeform tag to a navigable category ID, or null */
export function resolveTagCategory(tag) {
  const t = (tag || '').toLowerCase().trim();
  const slug = t.replace(/\s+/g, '-');
  if (VALID_CAT_IDS.has(slug)) return slug;
  if (VALID_CAT_IDS.has(t))    return t;
  return TAG_CATEGORY_MAP[t] || TAG_CATEGORY_MAP[slug] || null;
}
